import { useMemo, useState } from 'react'
import { useBusLocations } from '@/lib/queries'
import { DataError } from './DataError'

interface LiveBusesProps {
  routeCode: string
  lang: 'el' | 'en'
}

export function LiveBuses({ routeCode, lang }: LiveBusesProps) {
  const { data, isLoading, isError, error, refetch } = useBusLocations(routeCode)
  const [vehicleFilter, setVehicleFilter] = useState('')
  const buses = useMemo(() => (data || []).filter(bus => (
    Number.isFinite(parseFloat(bus.CS_LAT)) && Number.isFinite(parseFloat(bus.CS_LNG))
  )).filter(bus => bus.VEH_NO.includes(vehicleFilter.trim())), [data, vehicleFilter])

  return (
    <section className="live-buses" aria-label={lang === 'en' ? 'Live buses' : 'Ζωντανά λεωφορεία'}>
      <div className="live-buses-heading">
        <h2>{lang === 'en' ? 'Live buses' : 'Ζωντανά λεωφορεία'}</h2>
        <span>{lang === 'en' ? 'Route' : 'Διαδρομή'} {routeCode}</span>
      </div>

      <input
        type="search"
        className="vehicle-filter"
        value={vehicleFilter}
        onChange={event => setVehicleFilter(event.target.value)}
        aria-label={lang === 'en' ? 'Filter by vehicle number' : 'Φίλτρο με αριθμό οχήματος'}
        placeholder={lang === 'en' ? 'Vehicle number' : 'Αριθμός οχήματος'}
      />

      {isLoading ? (
        <div className="loading">{lang === 'en' ? 'Loading live buses...' : 'Φόρτωση ζωντανών λεωφορείων...'}</div>
      ) : isError ? (
        <DataError error={error} lang={lang} onRetry={() => void refetch()} />
      ) : buses.length === 0 ? (
        <p className="no-live-buses">
          {lang === 'en' ? 'No active buses are reporting on this route.' : 'Δεν υπάρχουν ενεργά λεωφορεία σε αυτή τη διαδρομή.'}
        </p>
      ) : (
        <div className="live-bus-list">
          {buses.map(bus => {
            const lat = parseFloat(bus.CS_LAT)
            const lng = parseFloat(bus.CS_LNG)
            const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
            return (
              <article className="live-bus-card" key={bus.VEH_NO}>
                <div>
                  <strong>{lang === 'en' ? 'Bus' : 'Λεωφορείο'} #{bus.VEH_NO}</strong>
                  <span>{lang === 'en' ? 'Route' : 'Διαδρομή'} {bus.ROUTE_CODE || routeCode}</span>
                  <span>{lang === 'en' ? 'Last update' : 'Τελευταία ενημέρωση'}: {bus.CS_DATE}</span>
                  <span>{lat}, {lng}</span>
                </div>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Open bus #${bus.VEH_NO} in Google Maps`}
                >
                  {lang === 'en' ? 'Open map' : 'Άνοιγμα χάρτη'}
                </a>
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}
